import React, { Fragment, useCallback, useState } from "react";

import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  Typography
} from "@packages/ds";
import { useIntl } from "react-intl";

import { LogLine } from "Api/observability/pipeline/types";
import * as Table from "ds/Table";
import { Tooltip } from "ds/Tooltip";
import { copyToClipboard } from "Libs/utils";
import { Flex } from "src/organization/pages/project/environment/metrics/components/tooltip/style";
import RoundLegend from "src/organization/pages/project/environment/metrics/http/components/RoundLegend";

import { severityToColor } from "../utils";

import * as S from "./LogsTable.styles";

type LogsTableProp = {
  logs: LogLine[];
  loadingMoreLogs: boolean;
  onLoadMore: () => void;
};

const logAttributes = [
  "severity",
  "service",
  "instance",
  "host",
  "command",
  "container_name"
];

const LogsTable = (props: LogsTableProp) => {
  const intl = useIntl();

  const [expandedLog, setExpandedLog] = useState<string>("");
  const [copiedToClipboard, setCopiedToClipboard] = useState<string>("");
  const [copiedToClipboardTimeout, setCopiedToClipboardTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const handleCopyToClipboard = log => {
    copyToClipboard(log.content);

    setCopiedToClipboard(log.ulid);

    if (copiedToClipboardTimeout) {
      clearTimeout(copiedToClipboardTimeout);
      setCopiedToClipboardTimeout(null);
    }

    setCopiedToClipboardTimeout(
      setTimeout(() => {
        setCopiedToClipboard("");
        setCopiedToClipboardTimeout(null);
      }, 1500)
    );
  };

  const toggleExpandedLog = useCallback(
    ulid => {
      if (expandedLog === ulid) {
        setExpandedLog("");
      } else {
        setExpandedLog(ulid);
      }
    },
    [expandedLog]
  );

  const handleLoadMore = () => {
    props.onLoadMore();
  };

  return (
    <>
      <S.LogsTableWrapper>
        <S.LogsTable>
          <Table.Table>
            <Table.Thead hasBorderBottom={true}>
              <Table.Th width="13%">Datetime</Table.Th>
              <Table.Th width="10%">Service</Table.Th>
              <Table.Th width="10%">Instance</Table.Th>
              <Table.Th width="62%">Content</Table.Th>
              <Table.Th width="5%"></Table.Th>
            </Table.Thead>
            <Table.Tbody height="650px">
              {props.logs.length ? (
                <>
                  {props.logs.map((log, i) => {
                    const isLineExpanded = expandedLog === log.ulid;

                    return (
                      <Fragment key={i}>
                        <S.TableTr size={isLineExpanded ? "auto" : "xs"}>
                          <Table.Td width="13%">
                            <Flex>
                              <div title={log.severity}>
                                <RoundLegend
                                  color={severityToColor(log.severity)}
                                />
                              </div>
                              {log.datetime}
                            </Flex>
                          </Table.Td>
                          <Table.Td width="10%">
                            <Typography.TextOverflow>
                              {log.service}
                            </Typography.TextOverflow>
                          </Table.Td>
                          <Table.Td width="10%">
                            <Typography.TextOverflow>
                              {log.instance}
                            </Typography.TextOverflow>
                          </Table.Td>
                          <Table.Td width="62%">
                            {!isLineExpanded && (
                              <S.LogContentWrapper>
                                <S.LogContent>{log.content}</S.LogContent>
                              </S.LogContentWrapper>
                            )}
                            {isLineExpanded && (
                              <S.ExpandedLogLine>
                                <S.LogContent>{log.content}</S.LogContent>
                                <S.LogAttributesList>
                                  {logAttributes.map(logAttr => (
                                    <S.LogAttribute key={logAttr}>
                                      <S.LogAttributeHeader>
                                        <S.LogAttributeKey>
                                          {logAttr}
                                        </S.LogAttributeKey>
                                        <S.LogAttributeSeparator></S.LogAttributeSeparator>
                                      </S.LogAttributeHeader>
                                      <S.LogAttributeValue>
                                        <span>{log[logAttr]}</span>
                                      </S.LogAttributeValue>
                                    </S.LogAttribute>
                                  ))}
                                </S.LogAttributesList>
                              </S.ExpandedLogLine>
                            )}
                          </Table.Td>
                          <Table.Td width="5%">
                            <Tooltip
                              tooltip="Copied to clipboard!"
                              open={copiedToClipboard === log.ulid}
                            >
                              <Button
                                content="icon"
                                onClick={() => {
                                  handleCopyToClipboard(log);
                                }}
                                variant="secondary"
                                title="Click to copy"
                              >
                                <CopyIcon />
                              </Button>
                            </Tooltip>
                            <Button
                              content="icon"
                              onClick={() => {
                                toggleExpandedLog(log.ulid);
                              }}
                              variant="secondary"
                              title={
                                isLineExpanded
                                  ? "Click to reduce"
                                  : "Click to expand"
                              }
                            >
                              {isLineExpanded ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )}
                            </Button>
                          </Table.Td>
                        </S.TableTr>
                      </Fragment>
                    );
                  })}
                  <Table.Tr size="xs" style={{ boxShadow: "none" }}>
                    <Table.Td width="100%">
                      <S.LogsFooter>
                        {!props.loadingMoreLogs ? (
                          <Button onClick={handleLoadMore}>Load more</Button>
                        ) : (
                          <Button disabled>Loading more</Button>
                        )}
                      </S.LogsFooter>
                    </Table.Td>
                  </Table.Tr>
                </>
              ) : (
                <Table.Tr size="xs">
                  <Table.Td width="100%">
                    {intl.formatMessage({ id: "no_data" })}
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table.Table>
        </S.LogsTable>
      </S.LogsTableWrapper>
    </>
  );
};

export default LogsTable;
